import React, { useState, useMemo, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Movie, Screening } from '../types'
import { Search, Filter, ArrowUpDown, ChevronLeft, ChevronRight, PlusCircle } from 'lucide-react'


interface SeatLayout {
  id: number
  screening_id: number
  layout_name: string
  rows: number
  cols: number
  seats: boolean[][]
}

interface OrganizerEventManagementProps {
  movies: Movie[]
  screeningsByMovie: Record<number, Screening[]>
  onViewDetails: (movieId: number, screeningId?: number) => void
  onCreateNew?: () => void
}

export default function OrganizerEventManagement({ movies, screeningsByMovie, onViewDetails, onCreateNew }: OrganizerEventManagementProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'valid' | 'expired' | 'unscheduled'>('all')
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'bookings'>('date')
  const [currentPage, setCurrentPage] = useState(1)
  const [seatLayouts, setSeatLayouts] = useState<Record<number, SeatLayout>>({})
  const [bookedByScreening, setBookedByScreening] = useState<Record<number, number>>({})
  const [splitByScreening, setSplitByScreening] = useState<Record<number, { silver_total: number; gold_total: number; diamond_total: number; silver_booked: number; gold_booked: number; diamond_booked: number }>>({})
  const itemsPerPage = 5

  // Function to calculate total capacity from seat layout
  const getTotalCapacity = (layout: SeatLayout | null): number => {
    if (!layout) return 100 // Default fallback

    let totalSeats = 0
    for (let i = 0; i < layout.rows; i++) {
      for (let j = 0; j < layout.cols; j++) {
        if (layout.seats[i][j]) totalSeats++
      }
    }
    return totalSeats
  }

  // Fetch seat layouts for screenings
  useEffect(() => {
    const fetchSeatLayouts = async () => {
      const allScreeningIds = Object.values(screeningsByMovie).flat().map(s => s.id)
      if (allScreeningIds.length === 0) return

      try {
        const { data: layouts, error } = await supabase
          .from('seat_layouts')
          .select('*')
          .in('screening_id', allScreeningIds)

        if (error) {
          console.error('Error fetching seat layouts:', error)
          return
        }

        if (layouts) {
          const layoutsByScreeningId: Record<number, SeatLayout> = {}
          layouts.forEach(layout => {
            layoutsByScreeningId[layout.screening_id] = layout as SeatLayout
          })
          setSeatLayouts(layoutsByScreeningId)
        }

        const { data: seatRows } = await supabase
          .from('seatssplitting')
          .select('screening_id,silver_total,gold_total,diamond_total,silver_booked,gold_booked,diamond_booked')
          .in('screening_id', allScreeningIds)
        const splitMap: Record<number, any> = {}
        for (const r of ((seatRows || []) as any[])) {
          splitMap[r.screening_id] = r
        }
        setSplitByScreening(splitMap)

        const { data: bookingsRows } = await supabase
          .from('booking_seats')
          .select('screening_id, seat_ids, seat_id')
          .in('screening_id', allScreeningIds)
        const { data: simpleBookings } = await supabase
          .from('bookings')
          .select('screening_id, status, seats')
          .in('screening_id', allScreeningIds)
        const bookingsBySid: Record<number, number> = {}
        for (const r of ((simpleBookings || []) as any[])) {
          if ((r.status || '') === 'confirmed') bookingsBySid[r.screening_id] = (bookingsBySid[r.screening_id] || 0) + Number(r.seats || 0)
        }
        const counts: Record<number, number> = {}
        for (const sid of allScreeningIds) {
          const bsForSid = ((bookingsRows || []) as any[]).filter(x => Number(x.screening_id) === sid)
          const bySeats = bsForSid.reduce((acc, row) => acc + (Array.isArray(row.seat_ids) ? (row.seat_ids as any[]).length : 1), 0)
          const split = splitMap[sid]
          const bySplit = split ? (split.silver_booked + split.gold_booked + split.diamond_booked) : 0
          const bySimple = bookingsBySid[sid] || 0
          counts[sid] = bySeats > 0 ? bySeats : (split ? bySplit : bySimple)
        }
        setBookedByScreening(counts)
      } catch (error) {
        console.error('Error fetching seat layouts:', error)
      }
    }

    fetchSeatLayouts()
  }, [screeningsByMovie])

  useEffect(() => {
    const ids = Object.values(screeningsByMovie).flat().map(s => s.id)
    if (!ids.length) return
    const subs: any[] = []
    for (const sid of ids) {
      const recompute = async () => {
        const { data: seatRows } = await supabase
          .from('seatssplitting')
          .select('screening_id,silver_total,gold_total,diamond_total,silver_booked,gold_booked,diamond_booked')
          .in('screening_id', ids)
        const splitMap: Record<number, any> = {}
        for (const r of ((seatRows || []) as any[])) splitMap[r.screening_id] = r
        setSplitByScreening(splitMap)

        const { data: seatBookings } = await supabase
          .from('booking_seats')
          .select('screening_id, seat_ids, seat_id')
          .in('screening_id', ids)
        const { data: simpleBookings } = await supabase
          .from('bookings')
          .select('screening_id, status, seats')
          .in('screening_id', ids)
        const simpleBySid: Record<number, number> = {}
        for (const r of ((simpleBookings || []) as any[])) {
          if ((r.status || '') === 'confirmed') simpleBySid[r.screening_id] = (simpleBySid[r.screening_id] || 0) + Number(r.seats || 0)
        }
        const counts: Record<number, number> = {}
        for (const id of ids) {
          const bsForSid = ((seatBookings || []) as any[]).filter(x => Number(x.screening_id) === id)
          const bySeats = bsForSid.reduce((acc, row) => acc + (Array.isArray(row.seat_ids) ? (row.seat_ids as any[]).length : 1), 0)
          const split = splitMap[id]
          const bySplit = split ? (split.silver_booked + split.gold_booked + split.diamond_booked) : 0
          const bySimple = simpleBySid[id] || 0
          counts[id] = bySeats > 0 ? bySeats : (split ? bySplit : bySimple)
        }
        setBookedByScreening(counts)
      }
      const chSeats = supabase
        .channel(`booking_seats_list_${sid}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'booking_seats', filter: `screening_id=eq.${sid}` }, recompute)
        .subscribe()
      const chSplit = supabase
        .channel(`seatssplitting_list_${sid}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'seatssplitting', filter: `screening_id=eq.${sid}` }, recompute)
        .subscribe()
      const chBookings = supabase
        .channel(`bookings_list_${sid}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings', filter: `screening_id=eq.${sid}` }, recompute)
        .subscribe()
      subs.push(chSeats, chSplit, chBookings)
    }
    return () => { for (const ch of subs) supabase.removeChannel(ch) }
  }, [screeningsByMovie])

  // Use actual database data instead of mock data
  const allEvents = useMemo(() => {
    const eventsList: any[] = []

    // 1. Add all screenings as individual events
    Object.entries(screeningsByMovie).forEach(([movieIdStr, screenings]) => {
      const mId = Number(movieIdStr)
      const movie = movies.find(m => m.id === mId)
      if (!movie) return

      screenings.forEach(screening => {
        const layout = seatLayouts[screening.id]
        const capFromLayout = getTotalCapacity(layout)
        let totalCapacity = 0

        if (capFromLayout > 0) {
          totalCapacity = capFromLayout
        } else {
          const split = splitByScreening[screening.id]
          if (split) {
            totalCapacity = split.silver_total + split.gold_total + split.diamond_total
          } else {
            totalCapacity = (screening.available_seats || 0)
          }
        }

        const booked = bookedByScreening[screening.id] || 0
        const isUpcoming = new Date(screening.start_time) >= new Date()

        eventsList.push({
          id: movie.id, // Keep movie ID for navigation
          uniqueId: `s-${screening.id}`, // Unique key for list
          name: movie.title,
          date: new Date(screening.start_time).toLocaleString(),
          status: (isUpcoming ? 'valid' : 'expired') as 'valid' | 'expired',
          bookings: `${booked}/${totalCapacity}`,
          image_url: movie.image_url,
          screeningId: screening.id // Track specific screening
        })
      })
    })

    // 2. Add movies with NO screenings as "Unscheduled" events
    movies.forEach(movie => {
      const screenings = screeningsByMovie[movie.id] || []
      if (screenings.length === 0) {
        eventsList.push({
          id: movie.id,
          uniqueId: `m-${movie.id}`,
          name: movie.title,
          date: 'Unscheduled',
          status: 'unscheduled' as const,
          bookings: '0/0',
          image_url: movie.image_url
        })
      }
    })

    return eventsList.filter(event =>
      event.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (statusFilter === 'all' || event.status === statusFilter)
    ).sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'date':
          if (a.date === 'Unscheduled') return 1
          if (b.date === 'Unscheduled') return -1
          return new Date(a.date).getTime() - new Date(b.date).getTime()
        case 'bookings':
          return parseInt(a.bookings.split('/')[0]) - parseInt(b.bookings.split('/')[0])
        default:
          return 0
      }
    })
  }, [movies, screeningsByMovie, searchTerm, statusFilter, sortBy, seatLayouts, bookedByScreening, splitByScreening])

  const totalPages = Math.ceil(allEvents.length / itemsPerPage)
  const paginatedEvents = allEvents.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const getStatusBadge = (status: 'valid' | 'expired' | 'unscheduled') => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"

    if (status === 'valid') {
      return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300`
    } else if (status === 'unscheduled') {
      return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300`
    } else {
      return `${baseClasses} bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300`
    }
  }

  return (
    <div className="flex-1 p-8">
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <h1 className="text-slate-900 dark:text-slate-50 text-3xl font-bold leading-tight tracking-tight">Event Management</h1>
          <button onClick={onCreateNew} className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-indigo-600 text-white text-sm font-bold leading-normal tracking-[0.015em] gap-2">
            <PlusCircle className="h-4 w-4" />
            <span className="truncate">Create New Event</span>
          </button>
        </header>

        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-900/70 rounded-xl shadow-sm mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex gap-2 items-center">
              {/* Search */}
              <div className="relative w-full max-w-xs">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                </div>
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full h-10 pl-10 pr-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>

              {/* Status Filter */}
              <button
                onClick={() => setStatusFilter(prev => {
                  if (prev === 'all') return 'valid'
                  if (prev === 'valid') return 'expired'
                  if (prev === 'expired') return 'unscheduled'
                  return 'all'
                })}
                className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium"
              >
                <Filter className="h-4 w-4" />
                {/* Adding the status filter */}
                <span>{statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</span>
              </button>
            </div>

            {/* Sort */}
            <button
              onClick={() => setSortBy(sortBy === 'date' ? 'name' : sortBy === 'name' ? 'bookings' : 'date')}
              className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium"
            >
              <ArrowUpDown className="h-4 w-4" />
              <span className="truncate">Sort by {sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}</span>
            </button>
          </div>

          {/* Table */}
          <div className="p-4">
            {paginatedEvents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-500 dark:text-slate-400">No events found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-800">
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Event Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Bookings</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {paginatedEvents.map((event) => (
                      <tr key={event.uniqueId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 h-[72px]">
                        <td className="px-4 py-2 text-slate-900 dark:text-slate-100 text-sm font-medium">
                          {event.name}
                        </td>
                        <td className="px-4 py-2 text-slate-500 dark:text-slate-400 text-sm">
                          {event.date}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <span className={getStatusBadge(event.status)}>
                            {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-slate-500 dark:text-slate-400 text-sm">
                          {event.bookings}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium">
                          <button onClick={() => onViewDetails(event.id, event.screeningId)} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center p-4 border-t border-slate-200 dark:border-slate-800">
              <nav className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex size-9 items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`text-sm font-medium flex size-9 items-center justify-center rounded-lg ${currentPage === pageNum
                        ? 'text-white bg-indigo-600'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}

                {totalPages > 5 && (
                  <>
                    <span className="text-sm font-medium flex size-9 items-center justify-center text-slate-500 dark:text-slate-400">
                      ...
                    </span>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="text-sm font-medium flex size-9 items-center justify-center text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex size-9 items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
